import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { AccessLogEntry } from '../lib/types';
import type { AccessLogEntryResponse } from '../lib/api-client';

function mapResponse(r: AccessLogEntryResponse): AccessLogEntry {
  return {
    object: 'credential_access_log',
    id: r.id,
    credential_id: r.credential_id,
    credential_name: r.credential_name,
    credential_service: r.credential_service,
    plan_id: r.plan_id,
    plan_title: r.plan_title,
    action: r.action,
    actor_id: r.actor_id,
    actor_name: r.actor_name,
    metadata: r.metadata,
    created_at: r.created_at,
  };
}

export function useAccessLog({ enabled = false }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const [entries, setEntries] = useState<AccessLogEntry[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const fetchInFlightRef = useRef(false);
  const hasLoadedOnce = useRef(false);

  const doFetch = useCallback(async (startingAfter?: string) => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    try {
      const query: Record<string, string> = { limit: '50' };
      if (startingAfter) query.starting_after = startingAfter;
      const res = await apiClientRef.current.listAccessLogs(query);
      const mapped = res.data.map(mapResponse);
      if (startingAfter) {
        setEntries((prev) => [...prev, ...mapped]);
      } else {
        setEntries(mapped);
      }
      setHasMore(res.has_more);
      hasLoadedOnce.current = true;
      setStatus('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load access log');
      if (!hasLoadedOnce.current) setStatus('error');
    } finally {
      fetchInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    void doFetch();
  }, [enabled, doFetch]);

  const loadMore = useCallback(() => {
    if (!hasMore || fetchInFlightRef.current) return;
    const lastId = entries[entries.length - 1]?.id;
    if (lastId) void doFetch(lastId);
  }, [hasMore, entries, doFetch]);

  const reload = useCallback(() => {
    setStatus('loading');
    void doFetch();
  }, [doFetch]);

  return { entries, status, error, hasMore, loadMore, reload };
}
